import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { ActionItemsFilters } from '@/types/schema';

import { GroupedActionItem } from '../types';

interface TaskBulkActionsContextType {
  selectedGroups: string[];
  setSelectedGroups: React.Dispatch<React.SetStateAction<string[]>>;
  groupBy: string | undefined;
  includeActionItemIds: string[];
  setIncludeActionItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  excludeActionItemIds: string[];
  setExcludeActionItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  groupedActionItems: GroupedActionItem[];
  filters: ActionItemsFilters;
}

const TaskBulkSelectContext = createContext<TaskBulkActionsContextType | undefined>(undefined);

export const useTaskBulkSelect = () => {
  const context = useContext(TaskBulkSelectContext);
  if (!context) {
    throw new Error('useTaskBulkSelect must be used within a TaskBulkSelectContextProvider');
  }
  return context;
};

interface TaskBulkSelectContextProviderProps {
  children: React.ReactNode;
  groupBy?: string;
  groupedActionItems: GroupedActionItem[];
  filters: ActionItemsFilters;
}

export const TaskBulkSelectContextProvider: React.FC<TaskBulkSelectContextProviderProps> = ({
  children,
  groupBy: initialGroupBy,
  groupedActionItems,
  filters,
}) => {
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [includeActionItemIds, setIncludeActionItemIds] = useState<string[]>([]);
  const [excludeActionItemIds, setExcludeActionItemIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedGroups([]);
    setIncludeActionItemIds([]);
    setExcludeActionItemIds([]);
  }, [initialGroupBy]);

  const value = useMemo(
    () => ({
      selectedGroups,
      setSelectedGroups,
      groupBy: initialGroupBy,
      includeActionItemIds,
      setIncludeActionItemIds,
      excludeActionItemIds,
      setExcludeActionItemIds,
      groupedActionItems,
      filters,
    }),
    [
      selectedGroups,
      initialGroupBy,
      includeActionItemIds,
      excludeActionItemIds,
      groupedActionItems,
      filters,
    ],
  );

  return <TaskBulkSelectContext.Provider value={value}>{children}</TaskBulkSelectContext.Provider>;
};
